/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { getDateFromNumber } from '../../../shared/components';
import {
  type InsightItemTypes,
  type ListRowState,
  type TrackedListRowState,
} from '../../../shared/types';
import {
  type SortState,
  type ViolationsTabSidePanelRowContextState,
} from '../data-layer/violationsTabTypes';

const TABS_MAPPING: Record<string, string> = {
  openCount: 'OPEN',
  mitigatedCount: 'RISK_ACCEPTED',
  inReviewCount: 'IN_PROGRESS',
  remediatedCount: 'REMEDIATED',
  closedCount: 'CLOSED',
} as const;

export const SORT_MAPPING: Record<string, string> = {
  NEWTOOLD: 'DESC',
  OLDTONEW: 'ASC',
  ATOZ: 'ASC',
  ZTOA: 'DESC',
  MOSTCRITICAL: 'DESC',
  LEASTCRITICAL: 'ASC',
  DESC: 'DESC',
  ASC: 'ASC',
} as const;

interface Pagination {
  max: number;
  offset: number;
}

type FilterMapValueType = string[] | number[] | undefined;

export const getFilterPayload = (
  filterMap: Record<string, FilterMapValueType>,
  activeTab: string | undefined,
  searchKey: string | null
) => {
  // Filters
  const sodStatuses = [TABS_MAPPING[activeTab ?? 'openCount']];

  // Risk payload
  const riskName = (filterMap.riskName ?? undefined) as string[];
  const actualRisk = (filterMap.ACTUALVSPOTENTIAL ?? undefined) as string[];
  const violationSource = (filterMap.VIOLATIONORIGIN ?? undefined) as string[];
  const violationAge = filterMap.VIOLATIONAGE ?? undefined;
  const riskPriority = (filterMap.priority ?? undefined) as string[];
  const riskType = (filterMap.riskType ?? undefined) as string[];
  const businessProcess: string[] | undefined =
    filterMap.BUSINESSPROCESS?.map((bp) => String(bp)) ?? undefined;
  const rulesetKeys: number[] | undefined =
    filterMap.RULESET?.map((rulesetKey) => Number(rulesetKey)) ?? undefined;
  const applicationName = (filterMap.APPLICATION ?? undefined) as string[];

  // User payload
  const userKeys: number[] | undefined =
    filterMap.systemUserName?.map((sysUName) => Number(sysUName)) ?? undefined;
  const userName: string[] | undefined =
    filterMap.username?.map((name) => String(name)) ?? undefined;
  const firstName = (filterMap.firstname ?? undefined) as string[];
  const lastName = (filterMap.lastname ?? undefined) as string[];
  const costCenter = (filterMap.costcenter ?? undefined) as string[];
  const employeeId: string[] | undefined =
    filterMap.employeeid?.map((empId) => String(empId)) ?? undefined;
  const jobCode: string[] | undefined =
    filterMap.jobCode?.map((jd) => String(jd)) ?? undefined;

  // Function payload
  const functionName = (filterMap.FUNCTION ?? undefined) as string[];
  const functionStatus = (filterMap.FUNCTIONSTATUS ?? undefined) as string[];

  // Account payload
  const accountKeys: number[] | undefined =
    filterMap.name?.map((endpoint) => Number(endpoint)) ?? undefined;
  const accountType: string[] | undefined =
    filterMap.accounttype?.map((type) => String(type)) ?? undefined;
  const userLock: string[] | undefined =
    filterMap.userlock?.map((lock) => String(lock)) ?? undefined;

  // Entitlement payload
  const entitlementValue = (filterMap.entitlement_value ??
    undefined) as string[];
  const entitlementType: string[] | undefined =
    filterMap.entitlementType?.map((lock) => String(lock)) ?? undefined;
  // const soxCritical = (filterMap.SOXCRITICAL ?? []) as string[];
  // const sysCritical = (filterMap.SYSCRITICAL ?? []) as string[];
  // const privileged = (filterMap.confidentiality ?? []) as string[];

  // Mitigating controls payload
  const mitigatingControlKeys: number[] | undefined =
    filterMap.MITIGATINGCTRLNAME?.map((mcControlValue) =>
      Number(mcControlValue)
    ) ?? undefined;
  const mcExpiryDate = filterMap.MCEXPIRYDATE ?? undefined;
  const mcAssignedStartDate = filterMap.MCSTARTDATE ?? undefined;
  const mcAssignedEndDate = filterMap.MCENDDATE ?? undefined;

  // Constructed payload
  const violationAgePayload =
    violationAge !== undefined
      ? (JSON.parse(violationAge[0] as string) as (number | null)[])
      : undefined;
  const mcExpiryDatePayload =
    mcExpiryDate !== undefined
      ? (JSON.parse(mcExpiryDate[0] as string) as (number | null)[])
      : undefined;
  const mcStartDatePayload =
    mcAssignedStartDate !== undefined
      ? (JSON.parse(mcAssignedStartDate[0] as string) as (number | null)[])
      : undefined;
  const mcEndDatePayload =
    mcAssignedEndDate !== undefined
      ? (JSON.parse(mcAssignedEndDate[0] as string) as (number | null)[])
      : undefined;

  const payload = {
    sodStatuses,
    risk: {
      riskName,
      actualRisk,
      riskPriority,
      riskType,
      businessProcess,
      violationSource,
      violationAge:
        violationAgePayload !== undefined
          ? {
              lessThanDays: violationAgePayload[1],
              moreThanDays: violationAgePayload[0],
            }
          : undefined,
    },
    ruleset: {
      rulesetKeys,
    },
    account: {
      accountKeys,
      accountType,
      userLock,
    },
    application: {
      applicationName,
    },
    user: {
      userKeys,
      userName,
      firstName,
      lastName,
      costCenter,
      employeeId,
      jobCode,
    },
    function: {
      functionName,
      status: functionStatus,
    },
    entitlement: {
      value: entitlementValue,
      entitlementType,
    },
    mitigatingControl: {
      mitigatingControlKeys,
      mcExpiryDate:
        mcExpiryDatePayload !== undefined
          ? {
              startDate:
                mcExpiryDatePayload[0] === null
                  ? mcExpiryDatePayload[0]
                  : getDateFromNumber(mcExpiryDatePayload[0]),
              endDate:
                mcExpiryDatePayload[1] === null
                  ? mcExpiryDatePayload[1]
                  : getDateFromNumber(mcExpiryDatePayload[1]),
            }
          : undefined,
      mcAssignedStartDate:
        mcStartDatePayload !== undefined
          ? {
              startDate:
                mcStartDatePayload[0] === null
                  ? mcStartDatePayload[0]
                  : getDateFromNumber(mcStartDatePayload[0]),
              endDate:
                mcStartDatePayload[1] === null
                  ? mcStartDatePayload[1]
                  : getDateFromNumber(mcStartDatePayload[1]),
            }
          : undefined,
      mcAssignedEndDate:
        mcEndDatePayload !== undefined
          ? {
              startDate:
                mcEndDatePayload[0] === null
                  ? mcEndDatePayload[0]
                  : getDateFromNumber(mcEndDatePayload[0]),
              endDate:
                mcEndDatePayload[1] === null
                  ? mcEndDatePayload[1]
                  : getDateFromNumber(mcEndDatePayload[1]),
            }
          : undefined,
    },
    globalSearch: `${searchKey ?? ''}`,
  };

  return payload;
};

export const getListPayload = (
  filterMap: Record<string, FilterMapValueType>,
  activeTab: string | undefined,
  searchKey: string | null,
  sortInAction: SortState,
  activeInsight: InsightItemTypes | null,
  pagination: Pagination
) => {
  const payload = {
    filters: {
      ...getFilterPayload(filterMap, activeTab, searchKey),
      insightRequests:
        activeInsight !== null
          ? [
              {
                id: activeInsight.id,
                filters: activeInsight.filters,
              },
            ]
          : undefined,
    },
    pagination: {
      offset: pagination.offset,
      max: pagination.max,
    },
    sort: {
      field: sortInAction?.value ?? 'sodkey',
      order: SORT_MAPPING[sortInAction?.order ?? SORT_MAPPING.DESC],
    },
  };

  return payload;
};

export const configureTracker = (
  listData: ListRowState[],
  isChecked = false
): TrackedListRowState[] =>
  listData.map((listRow, index) => ({
    ...listRow,
    tracker: {
      id: listRow.sodKey,
      isChecked,
      isRowDetailsOpen: false,
      isTransition: false,
      isFirstAddded: index === 0,
    },
  }));

export const getRowContext = (
  row: TrackedListRowState
): ViolationsTabSidePanelRowContextState => ({
  id: row.tracker.id,
  title: row.risk.riskName,
  displayName: row.user.displayName,
  username: row.user.username,
  isExpiredCount: row.appliedMcData?.totalMcExpiredCount ?? 0,
  isExpiringSoonCount: row.appliedMcData?.totalMcExpireSoon ?? 0,
  mitigatingControlCount: row.appliedMcData?.totalMcCount ?? 0,
});

export const findKeyValue = (obj: unknown, targetKey: string): string => {
  const targetKeyLower = targetKey.toLowerCase();
  const foundValues: string[] = [];

  if (typeof obj !== 'object' || obj === null) {
    return '-';
  }

  const objAsRecord = obj as Record<string, unknown>;

  // Check direct keys first
  const keys = Object.keys(objAsRecord);

  // eslint-disable-next-line @typescript-eslint/prefer-for-of, no-plusplus
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (key.toLowerCase() === targetKeyLower) {
      foundValues.push(String(objAsRecord[key]));

      // If not looking for functionName, return the first match
      if (key !== 'functionName') {
        return String(objAsRecord[key]);
      }
    }
  }

  // Recursively search nested objects
  Object.values(objAsRecord).forEach((value) => {
    if (typeof value === 'object' && value !== null) {
      const result = findKeyValue(value, targetKey);

      if (result !== '-') {
        foundValues.push(result);
      }
    }
  });

  // Return comma-separated values if found, otherwise '-'
  return foundValues.length > 0 ? foundValues.join(', ') : '-';
};

export const capitalize = (str: string): string => {
  // Handle null, undefined, or non-string inputs
  if (!str || typeof str !== 'string') {
    return '';
  }

  // Handle empty string
  if (str.length === 0) {
    return '';
  }

  // Handle single character
  if (str.length === 1) {
    return str.toUpperCase();
  }

  // Handle whitespace-only strings
  if (str.trim().length === 0) {
    return str; // Preserve original whitespace
  }

  // Standard capitalization
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
